<template>
<div>
    <p class="text-muted small">
        Firearms Licensing Approval Board — Medical/Physical Examination report.
        Patient name and address are pre-filled; the doctor completes the rest.
    </p>

    <b-form @submit.prevent="onSubmit" novalidate>
        <b-row>
            <b-col md="6">
                <b-form-group label="Mr./Ms./Mrs. (full name)">
                    <b-form-input v-model="form.name" />
                </b-form-group>
                <b-form-group label="Address">
                    <b-form-textarea rows="2" v-model="form.address" />
                </b-form-group>
                <b-form-group label="Application type">
                    <b-form-radio-group v-model="form.application_type" :options="[
                        { value: 'new', text: 'New Firearm Licence' },
                        { value: 'renewal', text: 'Renewal' }
                    ]" />
                </b-form-group>
                <b-form-group label="Patient type">
                    <b-form-radio-group v-model="form.patient_type" :options="[
                        { value: 'regular', text: 'Regular' },
                        { value: 'occasional', text: 'Occasional' },
                        { value: 'new', text: 'New' }
                    ]" />
                </b-form-group>
            </b-col>
            <b-col md="6">
                <b-row>
                    <b-col><b-form-group label="Height (cm)"><b-form-input type="number" v-model="form.height_cm" /></b-form-group></b-col>
                    <b-col><b-form-group label="Weight (kg)"><b-form-input type="number" v-model="form.weight_kg" /></b-form-group></b-col>
                </b-row>
                <b-form-group label="All limbs intact?">
                    <b-form-radio-group v-model="form.limbs_intact" :options="[{value:'yes',text:'Yes'},{value:'no',text:'No'}]" />
                </b-form-group>
                <b-form-group label="If NO, details">
                    <b-form-input v-model="form.limbs_details" />
                </b-form-group>
                <b-form-group label="Previously hospitalised?">
                    <b-form-radio-group v-model="form.hospitalised" :options="[{value:'yes',text:'Yes'},{value:'no',text:'No'}]" />
                </b-form-group>
                <b-form-group label="Hospital and year">
                    <b-form-input v-model="form.hospital_and_year" />
                </b-form-group>
            </b-col>
        </b-row>

        <h6 class="mt-2">Physical assessment</h6>
        <b-row>
            <b-col md="6" v-for="row in physicalRows" :key="row.field">
                <b-form-group :label="row.label">
                    <b-form-radio-group v-model="form[row.field]" :options="ratingOptions" />
                    <b-form-input class="mt-1" placeholder="Comments" v-model="form[row.field + '_comments']" />
                </b-form-group>
            </b-col>
        </b-row>

        <h6 class="mt-2">Mental health</h6>
        <b-row>
            <b-col md="6">
                <b-form-group label="General mental health">
                    <b-form-radio-group v-model="form.mental_health" :options="ratingOptions" />
                </b-form-group>
                <b-form-group label="Mental health comments">
                    <b-form-input v-model="form.mental_health_comments" />
                </b-form-group>
                <b-form-group label="Suicidal thoughts">
                    <b-form-radio-group v-model="form.suicidal_thoughts" :options="[{value:'present',text:'Present'},{value:'past',text:'Past'},{value:'never',text:'Never'}]" />
                </b-form-group>
                <b-form-group label="Suicidal attempt">
                    <b-form-radio-group v-model="form.suicidal_attempt" :options="[{value:'yes',text:'Yes'},{value:'no',text:'No'},{value:'never',text:'Never'}]" />
                </b-form-group>
                <b-form-group label="Depression">
                    <b-form-radio-group v-model="form.depression" :options="[{value:'present',text:'Present'},{value:'past',text:'Past'},{value:'never',text:'Never'}]" />
                </b-form-group>
            </b-col>
            <b-col md="6">
                <b-form-group label="Suicide in family"><b-form-radio-group v-model="form.suicide_family" :options="yesNoOpts" /></b-form-group>
                <b-form-group label="If yes, details"><b-form-input v-model="form.suicide_family_details" /></b-form-group>
                <b-form-group label="Homicide in family"><b-form-radio-group v-model="form.homicide_family" :options="yesNoOpts" /></b-form-group>
                <b-form-group label="If yes, details"><b-form-input v-model="form.homicide_family_details" /></b-form-group>
                <b-form-group label="Drug use (e.g. cocaine, marijuana)"><b-form-radio-group v-model="form.drug_use" :options="yesNoOpts" /></b-form-group>
                <b-form-group label="Alcohol use">
                    <b-form-radio-group v-model="form.alcohol_use" :options="[{value:'abuse',text:'Abuse'},{value:'occasional',text:'Occasional'},{value:'none',text:'Does not use'}]" />
                </b-form-group>
            </b-col>
        </b-row>

        <h6 class="mt-2">Risk factors</h6>
        <b-row>
            <b-col md="6">
                <b-form-group label="Considered impulsive"><b-form-radio-group v-model="form.impulsive" :options="yesNoOpts" /></b-form-group>
                <b-form-group label="Fainting / dizziness"><b-form-radio-group v-model="form.fainting" :options="yesNoOpts" /></b-form-group>
                <b-form-group label="Suffers from epilepsy"><b-form-radio-group v-model="form.epilepsy" :options="yesNoOpts" /></b-form-group>
                <b-form-group label="Debilitating pains / cramps"><b-form-radio-group v-model="form.debilitating" :options="yesNoOpts" /></b-form-group>
            </b-col>
            <b-col md="6">
                <b-form-group label="Prescribed medication impacting firearm use?"><b-form-radio-group v-model="form.medication_impact" :options="yesNoOpts" /></b-form-group>
                <b-form-group label="If YES — details &amp; whether long-term/permanent">
                    <b-form-textarea rows="2" v-model="form.medication_details" />
                </b-form-group>
                <b-form-group label="Other relevant medical condition">
                    <b-form-textarea rows="2" v-model="form.other_condition" />
                </b-form-group>
            </b-col>
        </b-row>

        <h6 class="mt-2">Medical facility</h6>
        <b-row>
            <b-col md="6">
                <b-form-group label="Facility name"><b-form-input v-model="form.facility_name" /></b-form-group>
                <b-form-group label="Facility address"><b-form-input v-model="form.facility_address" /></b-form-group>
                <b-form-group label="Facility telephone"><b-form-input v-model="form.facility_phone" /></b-form-group>
            </b-col>
            <b-col md="6">
                <b-form-group label="Date of examination"><b-form-datepicker v-model="form.exam_date" /></b-form-group>
                <b-form-group label="Medical practitioner (print)"><b-form-input v-model="form.practitioner_name" /></b-form-group>
                <b-form-group label="Registration No."><b-form-input v-model="form.registration_no" /></b-form-group>
            </b-col>
        </b-row>

        <div class="text-right mt-3">
            <b-button variant="secondary" @click="$emit('close')">Cancel</b-button>
            <b-button type="submit" variant="success">
                <i class="fa-solid fa-file-arrow-down"></i> Generate PDF
            </b-button>
        </div>
    </b-form>
</div>
</template>

<script>
import { formatDate, buildAddress, fullName, loadCoords } from "./pdfFiller"
import formPersistence from "./formPersistence"

export default {
    name: "falab-medical-form",
    mixins: [formPersistence],
    props: { demographics: { type: Object, default: () => ({}) } },
    data() {
        return {
            ratingOptions: [
                { value: "good", text: "Good" },
                { value: "fair", text: "Fair" },
                { value: "poor", text: "Poor" },
            ],
            yesNoOpts: [
                { value: "yes", text: "Yes" },
                { value: "no", text: "No" },
            ],
            physicalRows: [
                { field: "eyesight", label: "Eyesight" },
                { field: "hearing", label: "Hearing" },
                { field: "mobility", label: "Mobility" },
                { field: "blood_pressure", label: "Blood pressure" },
                { field: "motor_skills", label: "Motor skills" },
            ],
            form: {
                name: "",
                address: "",
                application_type: "new",
                patient_type: "new",
                height_cm: "",
                weight_kg: "",
                limbs_intact: "yes",
                limbs_details: "",
                hospitalised: "no",
                hospital_and_year: "",
                eyesight: "good", eyesight_comments: "",
                hearing: "good", hearing_comments: "",
                mobility: "good", mobility_comments: "",
                blood_pressure: "good", blood_pressure_comments: "",
                motor_skills: "good", motor_skills_comments: "",
                mental_health: "good", mental_health_comments: "",
                suicidal_thoughts: "never",
                suicidal_attempt: "never",
                depression: "never",
                suicide_family: "no", suicide_family_details: "",
                homicide_family: "no", homicide_family_details: "",
                drug_use: "no",
                alcohol_use: "none",
                impulsive: "no",
                fainting: "no",
                epilepsy: "no",
                debilitating: "no",
                medication_impact: "no",
                medication_details: "",
                other_condition: "",
                facility_name: "",
                facility_address: "",
                facility_phone: "",
                exam_date: new Date().toISOString().slice(0, 10),
                practitioner_name: "",
                registration_no: "",
            },
        }
    },
    watch: {
        demographics: {
            immediate: true,
            handler(d) {
                if (!d) return
                if (!this.form.name) this.form.name = fullName(d)
                if (!this.form.address) this.form.address = buildAddress(d)
            },
        },
    },
    methods: {
        // Approximate coordinates for falab-medical.pdf (US-Letter, 612x792).
        // Calibrate after dropping the real blank PDF in public/forms/.
        buildCoords() {
            return {
                name: { page: 0, x: 175, y: 660 },
                address: { page: 0, x: 100, y: 638 },
                app_new_check: { page: 0, x: 158, y: 600, type: "check" },
                app_renewal_check: { page: 0, x: 360, y: 600, type: "check" },
                patient_regular_check: { page: 0, x: 235, y: 480, type: "check" },
                patient_occasional_check: { page: 0, x: 360, y: 480, type: "check" },
                patient_new_check: { page: 0, x: 490, y: 480, type: "check" },

                eyesight_good: { page: 0, x: 290, y: 445, type: "check" },
                eyesight_fair: { page: 0, x: 360, y: 445, type: "check" },
                eyesight_poor: { page: 0, x: 430, y: 445, type: "check" },
                eyesight_comments: { page: 0, x: 200, y: 428 },

                hearing_good: { page: 0, x: 290, y: 395, type: "check" },
                hearing_fair: { page: 0, x: 360, y: 395, type: "check" },
                hearing_poor: { page: 0, x: 430, y: 395, type: "check" },
                hearing_comments: { page: 0, x: 200, y: 378 },

                mobility_good: { page: 0, x: 290, y: 345, type: "check" },
                mobility_fair: { page: 0, x: 360, y: 345, type: "check" },
                mobility_poor: { page: 0, x: 430, y: 345, type: "check" },
                mobility_comments: { page: 0, x: 200, y: 328 },

                bp_good: { page: 0, x: 290, y: 295, type: "check" },
                bp_fair: { page: 0, x: 360, y: 295, type: "check" },
                bp_poor: { page: 0, x: 430, y: 295, type: "check" },
                bp_comments: { page: 0, x: 200, y: 278 },

                motor_good: { page: 0, x: 290, y: 245, type: "check" },
                motor_fair: { page: 0, x: 360, y: 245, type: "check" },
                motor_poor: { page: 0, x: 430, y: 245, type: "check" },
                motor_comments: { page: 0, x: 200, y: 228 },

                limbs_yes: { page: 0, x: 360, y: 198, type: "check" },
                limbs_no: { page: 0, x: 430, y: 198, type: "check" },
                limbs_details: { page: 0, x: 220, y: 180 },
                height_cm: { page: 0, x: 165, y: 152 },
                weight_kg: { page: 0, x: 380, y: 152 },

                // Page 2
                hospitalised_yes: { page: 1, x: 360, y: 735, type: "check" },
                hospitalised_no: { page: 1, x: 430, y: 735, type: "check" },
                hospital_and_year: { page: 1, x: 175, y: 718 },
                mental_health_good: { page: 1, x: 290, y: 697, type: "check" },
                mental_health_fair: { page: 1, x: 360, y: 697, type: "check" },
                mental_health_poor: { page: 1, x: 430, y: 697, type: "check" },
                mental_health_comments: { page: 1, x: 175, y: 680 },

                suicidal_thoughts_present: { page: 1, x: 290, y: 650, type: "check" },
                suicidal_thoughts_past: { page: 1, x: 380, y: 650, type: "check" },
                suicidal_thoughts_never: { page: 1, x: 470, y: 650, type: "check" },

                suicidal_attempt_yes: { page: 1, x: 290, y: 622, type: "check" },
                suicidal_attempt_no: { page: 1, x: 380, y: 622, type: "check" },
                suicidal_attempt_never: { page: 1, x: 470, y: 622, type: "check" },

                depression_present: { page: 1, x: 290, y: 595, type: "check" },
                depression_past: { page: 1, x: 380, y: 595, type: "check" },
                depression_never: { page: 1, x: 470, y: 595, type: "check" },

                suicide_family_yes: { page: 1, x: 290, y: 568, type: "check" },
                suicide_family_no: { page: 1, x: 380, y: 568, type: "check" },
                suicide_family_details: { page: 1, x: 460, y: 568 },

                homicide_family_yes: { page: 1, x: 290, y: 540, type: "check" },
                homicide_family_no: { page: 1, x: 380, y: 540, type: "check" },
                homicide_family_details: { page: 1, x: 460, y: 540 },

                drug_use_yes: { page: 1, x: 290, y: 513, type: "check" },
                drug_use_no: { page: 1, x: 380, y: 513, type: "check" },

                alcohol_abuse: { page: 1, x: 290, y: 486, type: "check" },
                alcohol_occasional: { page: 1, x: 380, y: 486, type: "check" },
                alcohol_none: { page: 1, x: 480, y: 486, type: "check" },

                impulsive_yes: { page: 1, x: 290, y: 458, type: "check" },
                impulsive_no: { page: 1, x: 380, y: 458, type: "check" },
                fainting_yes: { page: 1, x: 290, y: 425, type: "check" },
                fainting_no: { page: 1, x: 380, y: 425, type: "check" },
                epilepsy_yes: { page: 1, x: 290, y: 395, type: "check" },
                epilepsy_no: { page: 1, x: 380, y: 395, type: "check" },
                debilitating_yes: { page: 1, x: 290, y: 365, type: "check" },
                debilitating_no: { page: 1, x: 380, y: 365, type: "check" },

                medication_impact_yes: { page: 1, x: 290, y: 322, type: "check" },
                medication_impact_no: { page: 1, x: 380, y: 322, type: "check" },
                medication_details: { page: 1, x: 110, y: 290 },
                other_condition: { page: 1, x: 110, y: 250 },

                facility_name: { page: 1, x: 280, y: 170 },
                facility_address: { page: 1, x: 280, y: 150 },
                facility_phone: { page: 1, x: 280, y: 130 },
                exam_date: { page: 1, x: 280, y: 110 },
                practitioner_name: { page: 1, x: 280, y: 90 },
                registration_no: { page: 1, x: 280, y: 70 },
            }
        },

        // Map form state to per-coord values (expanding radio buttons into individual check flags).
        buildValues() {
            const f = this.form
            return {
                name: f.name,
                address: f.address,

                app_new_check: f.application_type === "new",
                app_renewal_check: f.application_type === "renewal",

                patient_regular_check: f.patient_type === "regular",
                patient_occasional_check: f.patient_type === "occasional",
                patient_new_check: f.patient_type === "new",

                eyesight_good: f.eyesight === "good",
                eyesight_fair: f.eyesight === "fair",
                eyesight_poor: f.eyesight === "poor",
                eyesight_comments: f.eyesight_comments,
                hearing_good: f.hearing === "good",
                hearing_fair: f.hearing === "fair",
                hearing_poor: f.hearing === "poor",
                hearing_comments: f.hearing_comments,
                mobility_good: f.mobility === "good",
                mobility_fair: f.mobility === "fair",
                mobility_poor: f.mobility === "poor",
                mobility_comments: f.mobility_comments,
                bp_good: f.blood_pressure === "good",
                bp_fair: f.blood_pressure === "fair",
                bp_poor: f.blood_pressure === "poor",
                bp_comments: f.blood_pressure_comments,
                motor_good: f.motor_skills === "good",
                motor_fair: f.motor_skills === "fair",
                motor_poor: f.motor_skills === "poor",
                motor_comments: f.motor_skills_comments,

                limbs_yes: f.limbs_intact === "yes",
                limbs_no: f.limbs_intact === "no",
                limbs_details: f.limbs_details,
                height_cm: f.height_cm,
                weight_kg: f.weight_kg,

                hospitalised_yes: f.hospitalised === "yes",
                hospitalised_no: f.hospitalised === "no",
                hospital_and_year: f.hospital_and_year,

                mental_health_good: f.mental_health === "good",
                mental_health_fair: f.mental_health === "fair",
                mental_health_poor: f.mental_health === "poor",
                mental_health_comments: f.mental_health_comments,

                suicidal_thoughts_present: f.suicidal_thoughts === "present",
                suicidal_thoughts_past: f.suicidal_thoughts === "past",
                suicidal_thoughts_never: f.suicidal_thoughts === "never",

                suicidal_attempt_yes: f.suicidal_attempt === "yes",
                suicidal_attempt_no: f.suicidal_attempt === "no",
                suicidal_attempt_never: f.suicidal_attempt === "never",

                depression_present: f.depression === "present",
                depression_past: f.depression === "past",
                depression_never: f.depression === "never",

                suicide_family_yes: f.suicide_family === "yes",
                suicide_family_no: f.suicide_family === "no",
                suicide_family_details: f.suicide_family_details,

                homicide_family_yes: f.homicide_family === "yes",
                homicide_family_no: f.homicide_family === "no",
                homicide_family_details: f.homicide_family_details,

                drug_use_yes: f.drug_use === "yes",
                drug_use_no: f.drug_use === "no",
                alcohol_abuse: f.alcohol_use === "abuse",
                alcohol_occasional: f.alcohol_use === "occasional",
                alcohol_none: f.alcohol_use === "none",

                impulsive_yes: f.impulsive === "yes",
                impulsive_no: f.impulsive === "no",
                fainting_yes: f.fainting === "yes",
                fainting_no: f.fainting === "no",
                epilepsy_yes: f.epilepsy === "yes",
                epilepsy_no: f.epilepsy === "no",
                debilitating_yes: f.debilitating === "yes",
                debilitating_no: f.debilitating === "no",

                medication_impact_yes: f.medication_impact === "yes",
                medication_impact_no: f.medication_impact === "no",
                medication_details: f.medication_details,
                other_condition: f.other_condition,

                facility_name: f.facility_name,
                facility_address: f.facility_address,
                facility_phone: f.facility_phone,
                exam_date: formatDate(f.exam_date),
                practitioner_name: f.practitioner_name,
                registration_no: f.registration_no,
            }
        },

        async onSubmit() {
            try {
                const coords = (await loadCoords("falab-medical")) || this.buildCoords()
                await this.fillAndPersist({ form_key: "falab", title: "FALAB Medical Report" }, {
                    templateUrl: "/forms/falab-medical.pdf",
                    outputName: `falab-medical-${this.form.name || "patient"}.pdf`,
                    values: this.buildValues(),
                    coords,
                })
                this.$emit("close")
            } catch (e) {
                this.$bvToast.toast(e.message || "Could not generate PDF", {
                    title: "PDF generation failed",
                    variant: "danger",
                })
            }
        },
    },
}
</script>
